"use client";

import { create } from "zustand";
import { apiUrl } from "@/lib/aicheck/api-base";
import { findingFingerprint } from "@/lib/aicheck/history/fingerprint";
import type { ActionStatus } from "@/lib/aicheck/history/types";
import { authService } from "@/services/auth";

/**
 * Per-project map of finding fingerprints → action status, backing the
 * inline 対応/不要 buttons on finding cards. Loaded once per project from
 * server-side actions; decisions update it optimistically.
 */

export interface FindingDecisionInput {
  source: "ai" | "consistency";
  checkId: string;
  checkPoint: string;
  dayNumber: number | null;
  severity: string;
  finding: string;
  recommendation: string | null;
  aspectIds: string[];
  factIds: string[];
}

interface ActionDecisionsState {
  statusByProject: Record<string, Record<string, ActionStatus>>;
  loadedProjects: Record<string, boolean>;
  /**
   * Bumped on every decision write (finding-card `decide` or ledger-driven
   * `setStatus`). Consumers that hold their own copy of the actions — e.g. the
   * suggested-action ledger (ActionsPanel) — subscribe to this and refetch so
   * a decision made in one surface immediately reflects in the other.
   */
  version: number;
  loadForProject: (projectId: string) => Promise<void>;
  getStatus: (
    projectId: string,
    input: Pick<
      FindingDecisionInput,
      "source" | "checkId" | "checkPoint" | "dayNumber"
    >,
  ) => ActionStatus | null;
  decide: (
    projectId: string,
    input: FindingDecisionInput,
    status: Exclude<ActionStatus, "open" | "expired">,
    dismissReason?: string | null,
  ) => Promise<boolean>;
  /**
   * Write a status by fingerprint without hitting the API. Used by the ledger
   * to push its own decision into the finding-card map so the inline buttons
   * update in place.
   */
  setStatus: (
    projectId: string,
    fingerprint: string,
    status: ActionStatus,
  ) => void;
}

async function authedFetch(url: string, init?: RequestInit) {
  const token = await authService.getIdToken();
  if (!token) throw new Error("未認証です。再度サインインしてください。");
  return fetch(apiUrl(url), {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

export const useActionDecisionsStore = create<ActionDecisionsState>(
  (set, get) => ({
    statusByProject: {},
    loadedProjects: {},
    version: 0,

    loadForProject: async (projectId) => {
      if (!projectId || get().loadedProjects[projectId]) return;
      set((state) => ({
        loadedProjects: { ...state.loadedProjects, [projectId]: true },
      }));
      try {
        const res = await authedFetch(
          `/api/aicheck/projects/${encodeURIComponent(projectId)}/actions`,
        );
        if (!res.ok) return; // 404 = 未登録案件（アクション無し）
        const data = (await res.json()) as {
          actions?: Array<{ fingerprint: string; status: ActionStatus }>;
        };
        const map: Record<string, ActionStatus> = {};
        for (const action of data.actions ?? []) {
          map[action.fingerprint] = action.status;
        }
        set((state) => ({
          statusByProject: { ...state.statusByProject, [projectId]: map },
        }));
      } catch {
        // 取得失敗時はボタンが未判定状態で表示されるだけ
      }
    },

    getStatus: (projectId, input) => {
      const map = get().statusByProject[projectId];
      if (!map) return null;
      return map[findingFingerprint(input)] ?? null;
    },

    decide: async (projectId, input, status, dismissReason) => {
      try {
        const res = await authedFetch(
          `/api/aicheck/projects/${encodeURIComponent(projectId)}/findings/decide`,
          {
            method: "POST",
            body: JSON.stringify({
              ...input,
              status,
              ...(dismissReason ? { dismissReason } : {}),
            }),
          },
        );
        if (!res.ok) return false;
        get().setStatus(projectId, findingFingerprint(input), status);
        return true;
      } catch {
        return false;
      }
    },

    setStatus: (projectId, fingerprint, status) => {
      set((state) => ({
        version: state.version + 1,
        statusByProject: {
          ...state.statusByProject,
          [projectId]: {
            ...(state.statusByProject[projectId] ?? {}),
            [fingerprint]: status,
          },
        },
      }));
    },
  }),
);
